import React from "react";
import { Link } from "react-router-dom";
import { mediaUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

const Paragraphs = ({ text }) =>
  String(text || "")
    .split(/\n{2,}/)
    .filter(Boolean)
    .map((p, i) => (
      <p key={i} className="text-muted-foreground leading-relaxed mb-4 whitespace-pre-line">{p}</p>
    ));

const BLOCKS = {
  heading: ({ eyebrow, title, sub }) => (
    <div className="mb-2">
      {eyebrow && <p className="text-xs uppercase tracking-[0.25em] text-accent font-semibold mb-2">{eyebrow}</p>}
      {title && <h2 className="font-serif text-2xl sm:text-3xl font-bold leading-tight">{title}</h2>}
      {sub && <p className="mt-3 text-muted-foreground leading-relaxed">{sub}</p>}
    </div>
  ),
  richtext: ({ text }) => <div><Paragraphs text={text} /></div>,
  section: ({ heading, body }) => (
    <section>
      {heading && <h2 className="font-serif text-lg font-semibold mb-2">{heading}</h2>}
      <Paragraphs text={body} />
    </section>
  ),
  image: ({ url, caption, alt }) => (
    <figure className="rounded-2xl overflow-hidden border border-border">
      <img src={mediaUrl(url)} alt={alt || caption || ""} className="w-full h-auto object-cover" />
      {caption && <figcaption className="text-sm text-muted-foreground px-4 py-2">{caption}</figcaption>}
    </figure>
  ),
  gallery: ({ images = [] }) => (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {images.map((im, i) => (
        <img key={i} src={mediaUrl(typeof im === "string" ? im : im.url)} alt="" className="w-full h-40 object-cover rounded-xl border border-border" />
      ))}
    </div>
  ),
  stats: ({ items = [] }) => (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
      {items.map((s, i) => (
        <div key={i}><p className="font-serif text-4xl font-bold text-primary">{s.n}</p><p className="text-sm text-muted-foreground mt-1">{s.l}</p></div>
      ))}
    </div>
  ),
  cta: ({ text, href = "/" }) => (
    <div>
      <Link to={href}><Button className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground h-11 px-7">{text || "Learn more"} <ArrowRight className="ml-1.5 h-4 w-4" /></Button></Link>
    </div>
  ),
};

export const BLOCK_TYPES = [
  { type: "heading", label: "Heading", fields: ["eyebrow", "title", "sub"] },
  { type: "richtext", label: "Rich text", fields: ["text"] },
  { type: "section", label: "Section (heading + body)", fields: ["heading", "body"] },
  { type: "image", label: "Image", fields: ["url", "caption", "alt"] },
  { type: "gallery", label: "Gallery", fields: ["images"] },
  { type: "stats", label: "Stats", fields: ["items"] },
  { type: "cta", label: "Call to action", fields: ["text", "href"] },
];

export default function PageRenderer({ page }) {
  if (!page) return null;
  return (
    <div className="px-4 sm:px-6 lg:px-12 max-w-3xl mx-auto py-12 lg:py-16" data-testid={`cms-page-${page.slug}`}>
      {page.title && <h1 className="font-serif text-3xl lg:text-4xl font-bold mb-8">{page.title}</h1>}
      <div className="space-y-8">
        {(page.blocks || []).map((b, i) => {
          const Comp = BLOCKS[b.type];
          return Comp ? <div key={i} data-testid={`cms-block-${b.type}-${i}`}><Comp {...(b.props || {})} /></div> : null;
        })}
      </div>
    </div>
  );
}
